import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  IconButton,
  InputAdornment,
  CircularProgress,
} from '@mui/material'
import {
  Visibility,
  VisibilityOff,
  VpnKey as KeyIcon,
} from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { apiService } from '../../services/api'

interface ChangePasswordModalProps {
  open: boolean
  onClose: () => void
  onSuccess?: () => void
}

export const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation()
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showCurrentPassword, setShowCurrentPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)

  useEffect(() => {
    if (open) {
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
      setShowCurrentPassword(false)
      setShowNewPassword(false)
      setShowConfirmPassword(false)
      setError(null)
      setSuccessMessage(null)
    }
  }, [open])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setSuccessMessage(null)

    if (!currentPassword) {
      setError(t('auth.passwordRequired', 'Current password is required'))
      return
    }

    if (!newPassword || newPassword.length < 8) {
      setError(t('users.passwordMinLength', 'New password must be at least 8 characters long'))
      return
    }

    if (newPassword !== confirmPassword) {
      setError(t('users.passwordMismatch', 'Passwords do not match'))
      return
    }

    try {
      setLoading(true)
      const res = await apiService.changePassword({ currentPassword, newPassword }) as any
      if (res && res.success === false) {
        setError(res.error || t('auth.security.passwordChangeFailed', 'Failed to change password'))
        return
      }

      setSuccessMessage(t('settings.passwordUpdated', 'Password changed successfully'))
      setTimeout(() => {
        onSuccess?.()
        onClose()
      }, 1200)
    } catch (err: any) {
      console.error('Password change error:', err)
      setError(
        err.response?.data?.error ||
          err.message ||
          t('auth.security.passwordChangeFailed', 'Failed to change password')
      )
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
        <KeyIcon color="primary" />
        <Box>
          <Typography variant="h6" component="span" sx={{ fontWeight: 600 }}>
            {t('settings.changePassword', 'Change Password')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ display: 'block' }}>
            {t('settings.changePasswordDesc', 'Update your account password securely')}
          </Typography>
        </Box>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent dividers>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {successMessage && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {successMessage}
            </Alert>
          )}

          <TextField
            fullWidth
            label={t('settings.currentPassword', 'Current Password')}
            type={showCurrentPassword ? 'text' : 'password'}
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            margin="normal"
            disabled={loading}
            autoFocus
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label={t('auth.togglePasswordVisibility', 'toggle password visibility')}
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    edge="end"
                  >
                    {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <TextField
            fullWidth
            label={t('settings.newPassword', 'New Password')}
            type={showNewPassword ? 'text' : 'password'}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            margin="normal"
            disabled={loading}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label={t('auth.togglePasswordVisibility', 'toggle password visibility')}
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    edge="end"
                  >
                    {showNewPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <TextField
            fullWidth
            label={t('settings.confirmNewPassword', 'Confirm New Password')}
            type={showConfirmPassword ? 'text' : 'password'}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            margin="normal"
            disabled={loading}
            helperText={t('users.passwordMinLength', 'Password must be at least 8 characters')}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label={t('auth.togglePasswordVisibility', 'toggle password visibility')}
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    edge="end"
                  >
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={onClose} disabled={loading}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading || !currentPassword || !newPassword || !confirmPassword}
            startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <KeyIcon />}
          >
            {loading ? t('common.loading', 'Updating...') : t('settings.changePassword', 'Change Password')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}

export default ChangePasswordModal
